import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../services/api';
import './ScriptGeneratorPage.css';
// Modular components
import ScriptGeneratorHeader from '../components/ScriptGeneratorHeader';
import ScriptGeneratorControlPanel from '../components/ScriptGeneratorControlPanel';
import ScriptEditor from '../components/ScriptEditor';
import ScriptActions from '../components/ScriptActions';
import BatchInfo from '../components/BatchInfo';
import ScriptHistorySidebar from '../components/ScriptHistorySidebar';

const ScriptGeneratorPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [generatedScripts, setGeneratedScripts] = useState({});
  const [finalScript, setFinalScript] = useState('');
  const [scriptLanguage, setScriptLanguage] = useState('javascript');
  const [editorTheme, setEditorTheme] = useState('vs-dark');
  const [scriptHistory, setScriptHistory] = useState([]);
  const [currentScriptId, setCurrentScriptId] = useState(null);
  const [scriptMetadata, setScriptMetadata] = useState({});
  const [selectedTechnology, setSelectedTechnology] = useState('informatica');
  const [showHistorySidebar, setShowHistorySidebar] = useState(false);
  const { mappingResults, sourceSchema, targetSchema } = location.state || {};

  useEffect(() => {
    if (!mappingResults || !sourceSchema || !targetSchema) {
      toast.error('Required data not found. Please go back and generate ETL mapping first.');
    }
  }, [mappingResults, sourceSchema, targetSchema]);

  useEffect(() => {
    const loadScriptHistory = () => {
      try {
        const stored = localStorage.getItem('scriptHistory');
        if (stored) {
          const history = JSON.parse(stored);
          setScriptHistory(history);
          if (history.length > 0) {
            const latestScript = history[history.length - 1];
            setCurrentScriptId(latestScript.id);
            setScriptMetadata(latestScript.metadata);
            setScriptLanguage(getLanguageForTechnology(latestScript.metadata.technology));
          }
        }
      } catch (error) {
        console.error('Error loading script history:', error);
      }
    };
    loadScriptHistory();
  }, []);

  useEffect(() => {
    if (scriptHistory.length > 0) {
      localStorage.setItem('scriptHistory', JSON.stringify(scriptHistory));
    }
  }, [scriptHistory]);

  const getLanguageForTechnology = (technology) => {
    const tech = technology?.toLowerCase();
    if (tech === 'informatica') return 'sql';
    if (tech === 'pyspark') return 'python';
    return 'javascript';
  };

  useEffect(() => {
    if (selectedTechnology) {
      setScriptLanguage(getLanguageForTechnology(selectedTechnology));
    }
  }, [selectedTechnology]);

  const createBatches = (mappings, batchSize = 10) => {
    const batches = [];
    for (let i = 0; i < mappings.length; i += batchSize) {
      batches.push(mappings.slice(i, i + batchSize));
    }
    return batches;
  };

  const parseApiResponse = (response) => {
    const scripts = response.data.scripts || {};
    const metadata = response.data.metadata || {};
    const expectedFields = selectedTechnology.toLowerCase() === 'informatica'
      ? ['source_qualifier', 'expression_transformations', 'lookup_transformations', 'target_load_order', 'session_properties']
      : ['imports', 'dataframe_read', 'transformations', 'data_quality_checks', 'write_operations', 'error_handling'];
    const validatedScripts = {};
    expectedFields.forEach(field => {
      validatedScripts[field] = scripts[field] || '';
    });
    return { scripts: validatedScripts, metadata };
  };

  const generateBatchScripts = async (batchMappings, batchIndex) => {
    try {
      const response = await api.generateTransformationScripts({
        mapping_results: batchMappings,
        source_schema: sourceSchema,
        target_schema: targetSchema,
        technology: selectedTechnology
      });
      const parsedResponse = parseApiResponse(response);
      return { batchIndex, scripts: parsedResponse.scripts, metadata: parsedResponse.metadata };
    } catch (error) {
      throw new Error(`Batch ${batchIndex + 1} failed: ${error.message}`);
    }
  };

  const aggregateScripts = (batchResults) => {
    const aggregated = {
      source_qualifier: '',
      expression_transformations: '',
      lookup_transformations: '',
      target_load_order: '',
      session_properties: '',
      imports: '',
      dataframe_read: '',
      transformations: '',
      data_quality_checks: '',
      write_operations: '',
      error_handling: ''
    };
    batchResults.forEach(({ scripts }) => {
      Object.keys(scripts).forEach(key => {
        if (scripts[key] && typeof scripts[key] === 'string') {
          if (aggregated[key]) {
            aggregated[key] += '\n\n' + scripts[key];
          } else {
            aggregated[key] = scripts[key];
          }
        }
      });
    });
    return aggregated;
  };

  const getScriptStatistics = (aggregatedScripts) => {
    const stats = { totalLines: 0, nonEmptySections: 0, totalSections: 0 };
    Object.keys(aggregatedScripts).forEach(key => {
      if (aggregatedScripts[key] && typeof aggregatedScripts[key] === 'string') {
        const lines = aggregatedScripts[key].split('\n').length;
        stats.totalLines += lines;
        if (aggregatedScripts[key].trim()) {
          stats.nonEmptySections++;
        }
        stats.totalSections++;
      }
    });
    return stats;
  };

  const compileFinalScript = (aggregatedScripts) => {
    if (selectedTechnology.toLowerCase() === 'informatica') {
      const informaticaScripts = {
        source_qualifier: aggregatedScripts.source_qualifier,
        expression_transformations: aggregatedScripts.expression_transformations,
        lookup_transformations: aggregatedScripts.lookup_transformations,
        target_load_order: aggregatedScripts.target_load_order,
        session_properties: aggregatedScripts.session_properties
      };
      return `-- Informatica Transformation Scripts\n-- Generated by Data Engineering Copilot\n-- Technology: ${selectedTechnology}\n-- Total Mappings: ${mappingResults.length}\n-- Generated on: ${new Date().toISOString()}\n\n-- ============================================\n-- SOURCE QUALIFIER TRANSFORMATIONS\n-- ============================================\n${informaticaScripts.source_qualifier || '-- No source qualifier transformations'}\n\n-- ============================================\n-- EXPRESSION TRANSFORMATIONS\n-- ============================================\n${informaticaScripts.expression_transformations || '-- No expression transformations'}\n\n-- ============================================\n-- LOOKUP TRANSFORMATIONS\n-- ============================================\n${informaticaScripts.lookup_transformations || '-- No lookup transformations'}\n\n-- ============================================\n-- TARGET LOAD ORDER\n-- ============================================\n${informaticaScripts.target_load_order || '-- No target load order specified'}\n\n-- ============================================\n-- SESSION PROPERTIES\n-- ============================================\n${informaticaScripts.session_properties || '-- No session properties specified'}`;
    } else if (selectedTechnology.toLowerCase() === 'pyspark') {
      const pysparkScripts = {
        imports: aggregatedScripts.imports,
        dataframe_read: aggregatedScripts.dataframe_read,
        transformations: aggregatedScripts.transformations,
        data_quality_checks: aggregatedScripts.data_quality_checks,
        write_operations: aggregatedScripts.write_operations,
        error_handling: aggregatedScripts.error_handling
      };
      return `# PySpark Transformation Scripts\n# Generated by Data Engineering Copilot\n# Technology: ${selectedTechnology}\n# Total Mappings: ${mappingResults.length}\n# Generated on: ${new Date().toISOString()}\n\n# ============================================\n# IMPORTS\n# ============================================\n${pysparkScripts.imports || '# Standard PySpark imports'}\n\n# ============================================\n# DATAFRAME READ OPERATIONS\n# ============================================\n${pysparkScripts.dataframe_read || '# No dataframe read operations'}\n\n# ============================================\n# TRANSFORMATION LOGIC\n# ============================================\n${pysparkScripts.transformations || '# No transformations specified'}\n\n# ============================================\n# DATA QUALITY CHECKS\n# ============================================\n${pysparkScripts.data_quality_checks || '# No data quality checks'}\n\n# ============================================\n# WRITE OPERATIONS\n# ============================================\n${pysparkScripts.write_operations || '# No write operations'}\n\n# ============================================\n# ERROR HANDLING AND LOGGING\n# ============================================\n${pysparkScripts.error_handling || '# No error handling specified'}`;
    }
    return JSON.stringify(aggregatedScripts, null, 2);
  };

  const generateScriptsWithBatching = async () => {
    if (!mappingResults || mappingResults.length === 0) {
      toast.error('No mapping results available');
      return;
    }
    setIsGenerating(true);
    setGeneratedScripts({});
    setFinalScript('');
    try {
      const batches = createBatches(mappingResults, 10);
      setTotalBatches(batches.length);
      setCurrentBatch(0);
      const batchResults = [];
      for (let i = 0; i < batches.length; i++) {
        setCurrentBatch(i + 1);
        const batchResult = await generateBatchScripts(batches[i], i);
        batchResults.push(batchResult);
        setGeneratedScripts(prev => ({ ...prev, [`batch_${i}`]: batchResult.scripts }));
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      const aggregatedScripts = aggregateScripts(batchResults);
      const scriptStats = getScriptStatistics(aggregatedScripts);
      const finalCompiledScript = compileFinalScript(aggregatedScripts);
      setFinalScript(finalCompiledScript);
      const scriptMetadata = {
        name: `${selectedTechnology} Transformation Scripts`,
        totalLines: scriptStats.totalLines,
        nonEmptySections: scriptStats.nonEmptySections,
        totalSections: scriptStats.totalSections,
        batchesProcessed: batches.length,
        scriptStats: scriptStats
      };
      saveScriptToHistory(finalCompiledScript, scriptMetadata);
      toast.success(`Successfully generated ${selectedTechnology} transformation scripts from ${batches.length} batches! (${scriptStats.totalLines} lines, ${scriptStats.nonEmptySections}/${scriptStats.totalSections} sections populated)`);
      setScriptLanguage(getLanguageForTechnology(selectedTechnology));
    } catch (error) {
      toast.error(`Script generation failed: ${error.message}`);
    } finally {
      setIsGenerating(false);
      setCurrentBatch(0);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(finalScript);
      toast.success('Script copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const downloadScript = () => {
    const blob = new Blob([finalScript], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedTechnology.toLowerCase()}_transformation_scripts.${scriptLanguage === 'sql' ? 'sql' : 'py'}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Script downloaded successfully!');
  };

  const saveScriptToHistory = (script, metadata) => {
    const scriptId = `script_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const scriptEntry = {
      id: scriptId,
      script: script,
      metadata: {
        ...metadata,
        technology: selectedTechnology,
        totalMappings: mappingResults.length,
        generatedAt: new Date().toISOString(),
        sourceTables: sourceSchema?.tables?.length || 0,
        targetTables: targetSchema?.tables?.length || 0
      },
      createdAt: new Date().toISOString()
    };
    setScriptHistory(prev => [...prev, scriptEntry]);
    setCurrentScriptId(scriptId);
    setScriptMetadata(scriptEntry.metadata);
    return scriptId;
  };

  const loadScriptFromHistory = (scriptId) => {
    const scriptEntry = scriptHistory.find(entry => entry.id === scriptId);
    if (scriptEntry) {
      setFinalScript(scriptEntry.script);
      setCurrentScriptId(scriptId);
      setScriptMetadata(scriptEntry.metadata);
      setScriptLanguage(getLanguageForTechnology(scriptEntry.metadata.technology));
      toast.success(`Loaded script: ${scriptEntry.metadata.name || 'Untitled'}`);
    }
  };

  const deleteScriptFromHistory = (scriptId) => {
    setScriptHistory(prev => prev.filter(entry => entry.id !== scriptId));
    if (currentScriptId === scriptId) {
      setFinalScript('');
      setCurrentScriptId(null);
      setScriptMetadata({});
    }
    toast.success('Script deleted from history');
  };

  const goBackToETLTool = () => {
    navigate('/etl-tool');
  };

  if (!mappingResults || !sourceSchema || !targetSchema) {
    return (
      <div className="script-generator-page">
        <ScriptGeneratorHeader
          onBack={goBackToETLTool}
          mappingCount={0}
        />
        <div className="main-content no-history">
          <div className="left-content">
            <div className="error-container">
              <h2>❌ Missing Required Data</h2>
              <p>Please go back to the ETL Tool and generate mapping results first.</p>
              <button onClick={goBackToETLTool} className="btn-primary">
                ← Back to ETL Tool
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="script-generator-page">
      <div className={`main-content ${scriptHistory.length === 0 ? 'no-history' : ''} ${showHistorySidebar ? 'with-history-sidebar' : ''}`}>  
        <div className="left-content">
          <ScriptGeneratorHeader
            onBack={goBackToETLTool}
            mappingCount={mappingResults ? mappingResults.length : 0}
            onToggleHistory={() => setShowHistorySidebar(!showHistorySidebar)}
            showHistory={showHistorySidebar}
            scriptHistoryCount={scriptHistory.length}
          />
          <ScriptGeneratorControlPanel
            selectedTechnology={selectedTechnology}
            setSelectedTechnology={setSelectedTechnology}
            isGenerating={isGenerating}
            generateScripts={generateScriptsWithBatching}
            currentBatch={currentBatch}
            totalBatches={totalBatches}
          />
          {finalScript && (
            <ScriptEditor
              finalScript={finalScript}
              setFinalScript={setFinalScript}
              scriptLanguage={scriptLanguage}
              editorTheme={editorTheme}
              generatedScripts={generatedScripts}
              currentScriptId={currentScriptId}
              selectedTechnology={selectedTechnology}
            />
          )}
          <ScriptActions
            finalScript={finalScript}
            copyToClipboard={copyToClipboard}
            downloadScript={downloadScript}
          />
          <BatchInfo generatedScripts={generatedScripts} />
        </div>
        
        {/* Script History Sidebar */}
        {showHistorySidebar && (
          <ScriptHistorySidebar
            scriptHistory={scriptHistory}
            currentScriptId={currentScriptId}
            onLoadScript={loadScriptFromHistory}
            onDeleteScript={deleteScriptFromHistory}
            onClose={() => setShowHistorySidebar(false)}
          />
        )}
      </div>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastStyle={{ fontSize: '14px', borderRadius: '8px' }}
      />
    </div>
  );
};

export default ScriptGeneratorPage; 